// react
import React, { useCallback, useEffect, useRef, useState, useImperativeHandle, forwardRef } from 'react';
import { useEventListener } from 'ahooks';
import styles from './index.modules.scss';
import { Transition, Switch } from '@mantine/core';
import { useGenHistoryVisible, useViewDetailState, useComfirmModalState, useSelectRegulate } from './store/base';
import { eventBus } from '@starflow/app/helpers';
import { Icon } from '@shakkerai/components';
import { useGenHistoryHooks, useStorageHooks, useSharePicHooks } from './hooks/dataHooks';
import { NavBar, StoragePopover, Text, ConfirmModal } from '@shakkerai/pro-components';
import { TopBar } from './components/TopBar';
import { AblumList } from './components/AblumList';
import { RightMenuWrapper } from './components/RightMenuWrapper';
import { ImageDetailWrapper } from './components/ImageDetailWrapper';
import { translate } from '@shakkerai/util-i18n';
import { EVENT_LABEL_HISTORY_RELOAD, EVENT_LABEL_INIT_RESORT } from './utils/eventbus';
import { HistoryList } from './components/HistoryList';
import { mv } from '@shakkerai/report';
// import { SelectHover } from './components/SelectHover';
import HistoryHomeList from './history-home';
import { isOverseas } from '@shakkerai/pro-utils';
import { SHARE_PIC_FLAG } from '@shakkerai/pro-store';

export const GenHistoryList = forwardRef((props, ref) => {
  const { searchValueRef, sortValueRef, onDataUpdate, isHome, setIsHome } = props;
  const scrollContainerRef = useRef(null);
  const [currentDate, setCurrentDate] = useState(null);
  const currentDateRef = useRef(null);

  const { data, loading, loadingMore, noMore, reload, loadTotal } = useGenHistoryHooks(
    scrollContainerRef,
    searchValueRef,
    sortValueRef,
    currentDateRef,
  );

  // const {
  //   data: homeData,
  //   loading: homeLoading,
  //   loadingMore: homeLoadingMore,
  //   noMore: homeNoMore,
  //   reload: homeReload,
  // } = useGenHistoryHomeHooks(scrollContainerRef, sortValueRef);

  useImperativeHandle(ref, () => ({
    refresh: () => {
      // 拉取最新历史记录数量
      loadTotal?.();
    },
    reload: () => {
      reload();
    },
    // reloadHome: () => {
    //   homeReload();
    // },
  }));

  useEffect(() => {
    onDataUpdate(data);
  }, [data, onDataUpdate]);

  const handleCurrentDate = useCallback(
    (date) => {
      setCurrentDate(date);
      currentDateRef.current = date?.day;
      setIsHome(false);
      reload();
    },
    [reload, setIsHome],
  );

  if (isHome || !currentDate) {
    return (
      <HistoryHomeList
        onSelected={handleCurrentDate}
        scrollContainerRef={scrollContainerRef}
        noDateStr={translate('no_history_record')}
        sortValueRef={sortValueRef}
      />
    );
  }

  return (
    <div>
      <div className="px-[16px] mb-3">
        <span className="cursor-pointer" data-role="gen-history-trigger" onClick={() => setIsHome(true)}>
          <Icon className="icon-pro_arrow_right inline-block rotate-180 mr-[8px]" color="inherit" fontSize={18} />
          {currentDate?.dayShow}
        </span>
      </div>
      <HistoryList
        searchValueRef={searchValueRef}
        scrollContainerRef={scrollContainerRef}
        noSearchStr={translate('no_search_result_all')}
        noDateStr={translate('no_history_record')}
        source="all"
        deleteRefreshLabel="event_historyList"
        newRecordEventLabel="event_historyListOnNewRecords"
        data={data}
        loading={loading}
        loadingMore={loadingMore}
        noMore={noMore}
        reload={reload}
      />
    </div>
  );
});

export const GenHistory = React.memo(() => {
  const { genHistoryVisible, hideHistoryModelsBar } = useGenHistoryVisible();
  const { setSharePicStates, shareState } = useSharePicHooks(); // 获取公开图片switch开关状态

  // 查看图片详情
  const { currentViewedGroupInfo } = useViewDetailState();

  const { storageInfo, reloadStoreage } = useStorageHooks();

  const { modalVisible, content, subContent, closeConfirmModal, onConfirm } = useComfirmModalState();
  // 搜索状态保留
  const searchValueRef = useRef();
  const sortValueRef = useRef(-1);
  const [selectedTab, setSelectedTab] = useState(0);
  const [isSharePic, setIsSharePic] = useState(false);
  const [isHome, setIsHome] = useState(true);
  const genHistoryListRef = useRef();
  const ablumListRef = useRef(0);

  const containerRef = useRef(null);
  useEffect(() => {
    setIsSharePic(isOverseas);
    // eslint-disable-next-line
  }, [isOverseas]);

  useEffect(() => {
    const flag = localStorage.getItem('_IS_SHOW_SHARE_PIC_FLAG_');
    if (flag) {
      setIsSharePic(shareState);
    }
  }, [shareState]);

  const handleGlobalClick = useCallback(
    (event) => {
      if (containerRef.current?.contains(event.target)) {
        return;
      }

      if (event.target?.closest('[data-role="gen-history-trigger"]') || event.target?.closest('#viewerBackdrop')) {
        return;
      }

      genHistoryVisible && hideHistoryModelsBar();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [genHistoryVisible],
  );

  useEventListener('click', handleGlobalClick);
  const refresh = useCallback(() => {
    if (selectedTab === 0) {
      // reload是指页面的重新加载
      if (isHome) {
        genHistoryListRef.current?.reloadHome?.();
      } else {
        genHistoryListRef.current?.reload();
      }
    } else {
      ablumListRef.current?.reload();
    }
  }, [selectedTab, isHome]);

  useEffect(() => {
    if (genHistoryVisible) {
      refresh();
    }
    // eslint-disable-next-line
  }, [refresh, genHistoryVisible]);

  const justLoad = useCallback(() => {
    reloadStoreage();
    // refresh是指纯粹的数据获取
    genHistoryListRef.current?.refresh();
    // ablumListRef.current?.refresh();
  }, [reloadStoreage]);

  useEffect(() => {
    if (genHistoryVisible) {
      justLoad();
    }

    eventBus.on(EVENT_LABEL_HISTORY_RELOAD, justLoad);

    return () => {
      eventBus.off(EVENT_LABEL_HISTORY_RELOAD, justLoad);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [genHistoryVisible]);

  useEffect(() => {
    const init = () => {
      sortValueRef.current = -1;
    };

    eventBus.on(EVENT_LABEL_INIT_RESORT, init);
    return () => {
      eventBus.off(EVENT_LABEL_INIT_RESORT, init);
    };
  }, []);

  const { selectCount, selectMode } = useSelectRegulate();

  const [renderListData, setRenderListData] = useState([]);

  useEffect(() => {
    if (genHistoryVisible) {
      mv('tool.history.show');
    }
  }, [genHistoryVisible]);

  const setPicShareState = useCallback(async () => {
    const res = await setSharePicStates({ share: Number(!isSharePic) });
    const { code } = res;
    if (code === 0) {
      localStorage.setItem('_IS_SHOW_SHARE_PIC_FLAG_', SHARE_PIC_FLAG);
      setIsSharePic(!isSharePic);
    } else {
      console.log('api error');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSharePic]);

  const mainPageRender = () => {
    return (
      <>
        <div className={styles.head}>
          <NavBar
            leftSection={() => (
              <StoragePopover total={storageInfo?.threshold} used={storageInfo?.usedCount}>
                <Text fw={500} iconClass="" className="relative" size={14} iSize={16} color="#222">
                  {translate('history_title')}
                </Text>
              </StoragePopover>
            )}
            onClose={hideHistoryModelsBar}
            className={styles.navbar}
          >
            {selectMode && (!isHome || selectedTab === 1) ? (
              <Text color="#707070" size={12}>
                <Text color="#7BD0C2" size={12} className="mr-1">
                  {selectCount}
                </Text>
                {translate('history_selected')}
              </Text>
            ) : isOverseas ? (
              <>
                <Switch checked={isSharePic} onChange={setPicShareState} size="xs" />
                <Text color="#2D2D2D" size={12} className="ml-[6px] mr-3 font-medium not-italic">
                  {translate('public_setting')}
                </Text>
              </>
            ) : null}
          </NavBar>
          <div className={styles.controlBar}>
            <TopBar
              selectedTab={selectedTab}
              setSelectedTab={setSelectedTab}
              checked={false}
              listData={!isHome || selectedTab === 1 ? renderListData : []}
              isHome={isHome}
              onSearch={(value) => {
                searchValueRef.current = value;
                refresh();
              }}
              onSortChange={(s) => {
                sortValueRef.current = s;
                refresh();
              }}
              reloadStoreage={reloadStoreage}
            ></TopBar>
          </div>
        </div>
        <div
          className={`${styles.contentC}`}
          style={{
            left: -selectedTab * 560,
          }}
        >
          <GenHistoryList
            ref={genHistoryListRef}
            isHome={isHome}
            setIsHome={setIsHome}
            onDataUpdate={(data) => selectedTab === 0 && setRenderListData(data)}
            searchValueRef={searchValueRef}
            sortValueRef={sortValueRef}
          />

          {/* <AblumList /> */}
          <AblumList
            ref={ablumListRef}
            searchValueRef={searchValueRef}
            onDataUpdate={(data) => selectedTab === 1 && setRenderListData(data)}
            sortValueRef={sortValueRef}
          />
        </div>
        <RightMenuWrapper containerRef={containerRef} source={selectedTab === 0 ? 'history' : 'collection'} />
      </>
    );
  };

  return (
    <div ref={containerRef} className={`${styles.proGenHistoryC} proGenHistoryC`} data-visible={genHistoryVisible}>
      {mainPageRender()}

      <Transition mounted={currentViewedGroupInfo} transition="slide-left" duration={400} timingFunction="ease">
        {(_styles) => (
          <div style={_styles} className={styles.imageDetailWrapper}>
            <ImageDetailWrapper />
          </div>
        )}
      </Transition>

      <ConfirmModal
        opened={modalVisible}
        onClose={closeConfirmModal}
        content={content}
        subcontent={subContent}
        onConfirm={onConfirm}
      />
    </div>
  );
});
